import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import { NavLink, useLocation } from 'react-router-dom';
import { useRouteStore } from '../../store/RouteConfigStore';

// 라우트 타입 정의
interface RouteChild {
  label: string;
  path: string;
  readYn: boolean;
  children?: RouteChild[];
}

interface Route {
  label: string;
  path?: string;
  readYn: boolean;
  children?: RouteChild[];
}

// 포털 경로 prefix 처리: 다른 포털 prefix가 이미 포함된 경로는 rootPath를 붙이지 않고,
// /usp prefix는 제거한다 (USP는 prefix 없이 운영).
const PORTAL_PATH_SEGMENTS = ['tsp', 'dxp', 'biz', 'pms', 'usp'];

export const resolvePortalPath = (path: string | undefined, rootPath: string): string => {
  if (!path) return '';
  const firstSegment = path.split('/')[1];
  if (PORTAL_PATH_SEGMENTS.includes(firstSegment)) {
    return firstSegment === 'usp' ? path.replace(/^\/usp/, '') : path;
  }
  return `${rootPath}${path}`;
};

export const isExternalPortalPath = (resolvedPath: string, rootPath: string): boolean => {
  if (!resolvedPath) return false;
  // USP(루트)에서는 rootPath가 ''가 되기 때문에,
  // rootPath 기준 startsWith() 비교로 판단하면 오동작할 수 있음.
  // 따라서 path의 첫 세그먼트로 외부 포털 여부를 판단한다.
  const resolvedSeg = resolvedPath.split('/').filter(Boolean)[0] || 'usp';
  const currentRootSeg = rootPath
    ? rootPath.replace(/^\//, '').split('/')[0] || 'usp'
    : 'usp';

  return resolvedSeg !== currentRootSeg;
};

// TopNavigationBar Props 타입
interface TopNavigationBarProps {
  setIsMenuActive: (value: boolean) => void;
  setIsFullMenuActive: (value: boolean) => void;
}

export const TopNavigationBar = ({ setIsMenuActive, setIsFullMenuActive }: TopNavigationBarProps) => {
  const { routes, portal } = useRouteStore();
  const location = useLocation();

  const rootPath = useMemo(() => portal === 'PORTAL_TSP' ? '/tsp' : portal === 'PORTAL_PMS' ? '/biz' : '', [portal]);

  // 필터링된 routes 메모이제이션
  const filteredRoutes = useMemo(() =>
    routes.filter(r => r.readYn) as Route[],
    [routes]
  );

  // 경로 매칭 헬퍼: 경로 구분자(/)를 고려한 정확한 매칭
  const isPathMatch = useCallback((currentPath: string, targetPath: string) => {
    if (!targetPath) return false;
    if (currentPath === targetPath) return true;
    // targetPath가 이미 /로 끝나면 그대로, 아니면 / 추가하여 경계 체크
    const prefix = targetPath.endsWith('/') ? targetPath : targetPath + '/';
    return currentPath.startsWith(prefix);
  }, []);

  // 현재 경로와 가장 길게 매칭되는 메뉴의 인덱스를 찾는 함수
  // 1차 메뉴 자체 path + 2차/3차 자식 path 모두 독립적으로 검사
  const activeRouteIndex = useMemo(() => {
    const currentPath = location.pathname;
    let bestIndex = -1;
    let longestMatchLength = 0;

    filteredRoutes.forEach((route, i) => {
      const menuPath = resolvePortalPath(route.path, rootPath);

      // 1차 메뉴 자체 경로 매칭 체크
      if (menuPath && isPathMatch(currentPath, menuPath)) {
        if (menuPath.length > longestMatchLength) {
          bestIndex = i;
          longestMatchLength = menuPath.length;
        }
      }

      // 2차, 3차 자식 메뉴 경로 매칭 체크 (부모 path와 무관하게 독립적으로 검사)
      if (route.children) {
        route.children.forEach((child) => {
          const childPath = resolvePortalPath(child.path, rootPath);
          if (childPath && isPathMatch(currentPath, childPath)) {
            if (childPath.length > longestMatchLength) {
              bestIndex = i;
              longestMatchLength = childPath.length;
            }
          }

          if (child.children) {
            child.children.forEach((depth3) => {
              const depth3Path = resolvePortalPath(depth3.path, rootPath);
              if (depth3Path && isPathMatch(currentPath, depth3Path)) {
                if (depth3Path.length > longestMatchLength) {
                  bestIndex = i;
                  longestMatchLength = depth3Path.length;
                }
              }
            });
          }
        });
      }
    });

    return bestIndex;
  }, [location.pathname, rootPath, filteredRoutes, isPathMatch]);

  const handleMouseEnter = useCallback(() => setIsMenuActive(true), [setIsMenuActive]);
  const handleMouseLeave = useCallback(() => setIsMenuActive(false), [setIsMenuActive]);
  const handleSearchClick = useCallback(() => {
    window.open(`${process.env.REACT_APP_DOMAIN}/search`, '_blank');
  }, []);
  const handleFullMenuClick = useCallback(() => setIsFullMenuActive(true), [setIsFullMenuActive]);

  return (
    <>
      <nav className="gnb-w" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <ul className="gnb">
          {filteredRoutes.map((route, i) => (
            <GnbItem
              key={i}
              route={route}
              rootPath={rootPath}
              currentPath={location.pathname}
              isActive={i === activeRouteIndex}
            />
          ))}
        </ul>
      </nav>

      <div className='header-body-right'>
        <button type='button' title='통합검색' className='search-btn' onClick={handleSearchClick}>
          <i className='icon icon-search'></i>
        </button>
        <button title='전체메뉴 열기' type='button' className='ham-btn ham-btn-pc' onClick={handleFullMenuClick}>
          <span className='ham-btn-line'></span>
        </button>
      </div>
    </>
  );
};

// GNB 메뉴 아이템 컴포넌트
interface GnbItemProps {
  route: Route;
  rootPath: string;
  currentPath: string;
  isActive: boolean;
}

const GnbItem = ({ route, rootPath, currentPath, isActive }: GnbItemProps) => {
  const filteredChildren = useMemo(() =>
    route.children?.filter(f => f.readYn && f.label !== '사업신청') || [],
    [route.children]
  );

  const routePath = resolvePortalPath(route.path, rootPath);

  return (
    <li className="gnb-li">
      {isExternalPortalPath(routePath, rootPath) ? (
        <a href={routePath || '#'} className={isActive ? "gnb-link active" : "gnb-link"}>
          <span>{route.label}</span>
        </a>
      ) : (
        <NavLink
          to={routePath || '#'}
          className={() => isActive ? "gnb-link active" : "gnb-link"}
        >
          <span>{route.label}</span>
        </NavLink>
      )}

      <div className="lnb">
        <ul className="lnb-w">
          {filteredChildren.map((child, j) => {
            const childPath = resolvePortalPath(child.path, rootPath);
            const isChildActive = childPath ? (currentPath === childPath || currentPath.startsWith(childPath + '/')) : false;
            const childExternal = isExternalPortalPath(childPath, rootPath);

            return (
              <li key={j} className="lnb-li">
                {childExternal ? (
                  <a href={childPath} className={isChildActive ? "lnb-link active" : "lnb-link"}>
                    <span>{child.label}</span>
                  </a>
                ) : (
                  <NavLink to={childPath} className={() => isChildActive ? "lnb-link active" : "lnb-link"}>
                    <span>{child.label}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </li>
  );
};

// 높이 애니메이션 커스텀 훅 (모바일에서만)
const useHeightAnimation = (
  isActive: boolean,
  isMobile: boolean,
  ref: React.RefObject<HTMLUListElement>
): string | number => {
  const [height, setHeight] = useState<string | number>(0);
  const isAnimatingRef = useRef(false);

  useLayoutEffect(() => {
    // PC에서는 height 제어 안 함
    if (!isMobile) {
      setHeight('auto');
      return;
    }

    const el = ref.current;
    if (!el) return;

    if (isActive) {
      // CSS visibility: hidden 상태에서 높이 측정을 위해 임시 스타일 변경
      const savedVisibility = el.style.visibility;
      const savedOpacity = el.style.opacity;

      el.style.height = 'auto';
      el.style.visibility = 'visible';
      el.style.opacity = '1';

      // 강제 reflow 후 측정
      void el.offsetHeight;
      const targetHeight = el.scrollHeight;

      // 0에서 시작
      el.style.height = '0px';
      el.style.visibility = savedVisibility;
      el.style.opacity = savedOpacity;

      // 강제 reflow 후 즉시 애니메이션 시작
      void el.offsetHeight;
      isAnimatingRef.current = true;
      setHeight(targetHeight);
    } else {
      setHeight(0);
    }
  }, [isActive, isMobile, ref]);

  // transitionend 처리
  useEffect(() => {
    const el = ref.current;
    if (!isMobile || !el) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      // height transition만 처리
      if (e.propertyName !== 'height') return;

      if (isAnimatingRef.current && height !== 0) {
        // transition 비활성화 후 auto로 변경 (줄어드는 현상 방지)
        el.style.transition = 'none';
        setHeight('auto');
        isAnimatingRef.current = false;

        // 다음 프레임에서 transition 복원
        requestAnimationFrame(() => {
          el.style.transition = '';
        });
      }
    };

    el.addEventListener('transitionend', handleTransitionEnd);
    return () => el.removeEventListener('transitionend', handleTransitionEnd);
  }, [isMobile, ref, height]);

  return height;
};

// 전체메뉴 Props 타입
interface FullMenuProps {
  closeMenu: () => void;
  isMobile?: boolean;
}

// 전체메뉴 컴포넌트 (PC/모바일 공용)
export const FullMenu = ({ closeMenu, isMobile = false }: FullMenuProps) => {
  const { routes, portal } = useRouteStore();
  const rootPath = useMemo(() => portal === 'PORTAL_TSP' ? '/tsp' : portal === 'PORTAL_PMS' ? '/biz' : '', [portal]);
  const [activeDepth2, setActiveDepth2] = useState<number | null>(null);
  const [activeDepth3, setActiveDepth3] = useState<string | null>(null);

  // ESC 키 누르면 메뉴 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeMenu]);

  // 필터링된 routes를 메모이제이션
  const filteredRoutes = useMemo(() =>
    routes.filter(r => r.readYn) as Route[],
    [routes]
  );

  // 2차 메뉴 토글
  const handleDepth2Toggle = useCallback((index: number) => {
    setActiveDepth2(prev => {
      if (prev === index) {
        setActiveDepth3(null);
        return null;
      }
      setActiveDepth3(null);
      return index;
    });
  }, []);

  // 3차 메뉴 토글
  const handleDepth3Toggle = useCallback((key: string) => {
    setActiveDepth3(prev => prev === key ? null : key);
  }, []);

  return (
    <ul className='fullmenu'>
      {filteredRoutes.map((route, i) => (
        <Depth1Item
          key={i}
          index={i}
          route={route}
          rootPath={rootPath}
          isMobile={isMobile}
          isActive={activeDepth2 === i}
          activeDepth3={activeDepth3}
          onToggle={handleDepth2Toggle}
          onDepth3Toggle={handleDepth3Toggle}
          closeMenu={closeMenu}
        />
      ))}
    </ul>
  );
};

// 1차 메뉴 아이템 Props 타입
interface Depth1ItemProps {
  index: number;
  route: Route;
  rootPath: string;
  isMobile: boolean;
  isActive: boolean;
  activeDepth3: string | null;
  onToggle: (index: number) => void;
  onDepth3Toggle: (key: string) => void;
  closeMenu: () => void;
}

// 1차 메뉴 아이템 컴포넌트
const Depth1Item = ({
  index,
  route,
  rootPath,
  isMobile,
  isActive,
  activeDepth3,
  onToggle,
  onDepth3Toggle,
  closeMenu,
}: Depth1ItemProps) => {
  const depth2Ref = useRef<HTMLUListElement>(null);
  const height = useHeightAnimation(isActive, isMobile, depth2Ref);

  // 필터링된 children을 메모이제이션
  const filteredChildren = useMemo(() =>
    route.children?.filter((child) => child.readYn) || [],
    [route.children]
  );

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(index);
  }, [onToggle, index]);

  return (
    <li className={isActive ? 'active' : ''}>
      <p className='fullmenu-depth1'>{route.label}</p>
      <button
        type='button'
        className='fullmenu-panel'
        onClick={handleToggle}
      >
        <span>{route.label}</span>
      </button>
      <ul
        className='fullmenu-depth2-w'
        ref={depth2Ref}
        style={isMobile ? { height: typeof height === 'number' ? `${height}px` : height } : undefined}
      >
        {filteredChildren.map((child, j) => {
          const depth3Key = `${index}-${j}`;
          const hasDepth3 = !!(child.children && child.children.length > 0);

          return (
            <Depth2Item
              key={j}
              child={child}
              rootPath={rootPath}
              isMobile={isMobile}
              depth3Key={depth3Key}
              hasDepth3={hasDepth3}
              isDepth3Active={activeDepth3 === depth3Key}
              onDepth3Toggle={onDepth3Toggle}
              closeMenu={closeMenu}
            />
          );
        })}
      </ul>
    </li>
  );
};

// 2차 메뉴 아이템 Props 타입
interface Depth2ItemProps {
  child: RouteChild;
  rootPath: string;
  isMobile: boolean;
  depth3Key: string;
  hasDepth3: boolean;
  isDepth3Active: boolean;
  onDepth3Toggle: (key: string) => void;
  closeMenu: () => void;
}

// 2차 메뉴 아이템 컴포넌트
const Depth2Item = ({
  child,
  rootPath,
  isMobile,
  depth3Key,
  hasDepth3,
  isDepth3Active,
  onDepth3Toggle,
  closeMenu,
}: Depth2ItemProps) => {
  const depth3Ref = useRef<HTMLUListElement>(null);
  const height = useHeightAnimation(isDepth3Active, isMobile, depth3Ref);

  // 필터링된 depth3 children을 메모이제이션
  const filteredDepth3 = useMemo(() =>
    child.children?.filter((depth3) => depth3.readYn) || [],
    [child.children]
  );

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDepth3Toggle(depth3Key);
  }, [onDepth3Toggle, depth3Key]);

  const liClassName = useMemo(() =>
    [hasDepth3 ? 'has-depth3' : '', isDepth3Active ? 'active' : ''].filter(Boolean).join(' '),
    [hasDepth3, isDepth3Active]
  );

  const childPath = resolvePortalPath(child.path, rootPath);
  const childExternal = isExternalPortalPath(childPath, rootPath);

  return (
    <li className={liClassName}>
      {childExternal ? (
        <a href={childPath} className='fullmenu-depth2' onClick={closeMenu}>
          <span>{child.label}</span>
        </a>
      ) : (
        <NavLink
          to={childPath}
          className='fullmenu-depth2'
          onClick={closeMenu}
        >
          <span>{child.label}</span>
        </NavLink>
      )}
      {hasDepth3 && (
        <button
          type='button'
          className='fullmenu-depth2-panel'
          onClick={handleToggle}
        >
          <span>{child.label}</span>
        </button>
      )}
      {hasDepth3 && filteredDepth3.length > 0 && (
        <ul
          className='fullmenu-depth3-w'
          ref={depth3Ref}
          style={isMobile ? { height: typeof height === 'number' ? `${height}px` : height } : undefined}
        >
          {filteredDepth3.map((depth3, k) => {
            const d3Path = resolvePortalPath(depth3.path, rootPath);
            const d3External = isExternalPortalPath(d3Path, rootPath);
            return (
              <li key={k}>
                {d3External ? (
                  <a href={d3Path} className='fullmenu-depth3' onClick={closeMenu}>
                    <span>{depth3.label}</span>
                  </a>
                ) : (
                  <NavLink
                    to={d3Path}
                    className='fullmenu-depth3'
                    onClick={closeMenu}
                  >
                    <span>{depth3.label}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

export default TopNavigationBar;